import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateQuizModal from './components/CreateQuizModal';
import {
  createQuiz, getQuizzesByManager, getQuizAttempts, deleteQuiz
} from '../../api_service';

const pctColor = (pct) => {
  if (pct >= 70) return 'text-success';
  if (pct >= 40) return 'text-warning';
  return 'text-error';
};

const QuizManagement = () => {
  const navigate = useNavigate();
  const managerId = sessionStorage.getItem('userId');

  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadQuizzes(); }, []);

  const loadQuizzes = async () => {
    setLoading(true);
    try {
      const res = await getQuizzesByManager(managerId);
      setQuizzes(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setQuizzes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAttempts = async (quizId) => {
    try {
      const res = await getQuizAttempts(quizId);
      setAttempts(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setAttempts([]);
    }
  };

  const handleQuizClick = async (item) => {
    setSelectedQuiz(item);
    await loadAttempts(item.quiz.quizId);
  };

  const handleCreateQuiz = async (payload) => {
    await createQuiz(managerId, payload);
    await loadQuizzes();
  };

  const handleDelete = async (e, quizId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this quiz and all attempts?')) return;
    await deleteQuiz(quizId);
    if (selectedQuiz?.quiz?.quizId === quizId) {
      setSelectedQuiz(null);
      setAttempts([]);
    }
    await loadQuizzes();
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning' };
    return { text: `${diff}d left`, cls: 'text-muted-foreground' };
  };

  const totalAttempts = quizzes.reduce((s, q) => s + (q.completed || 0), 0);
  const avgScore = (() => {
    // average across currently loaded attempts of selected quiz, fallback 0
    const done = attempts.filter(a => a.status === 'COMPLETED' && a.score != null);
    if (done.length === 0) return 0;
    const tm = selectedQuiz?.quiz?.totalMarks || 0;
    if (!tm) return 0;
    const avgPct = done.reduce((s, a) => s + (a.score / tm) * 100, 0) / done.length;
    return Math.round(avgPct);
  })();

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="manager"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Quiz Management</h1>
            <p className="text-sm text-muted-foreground mt-1">Create assessments, track attempts, and review trainee scores</p>
          </div>
          <Button onClick={() => setShowCreate(true)} iconName="Plus" iconSize={16}>
            Create Quiz
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Quizzes', value: quizzes.length, icon: 'FileQuestion', color: 'text-primary' },
            { label: 'Total Attempts', value: totalAttempts, icon: 'Users', color: 'text-accent' },
            { label: 'Avg Score (selected)', value: `${avgScore}%`, icon: 'BarChart3', color: 'text-success' },
          ].map(card => (
            <div key={card.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name={card.icon} size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{card.value}</p>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Quiz list */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Quizzes ({quizzes.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : quizzes.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="FileQuestion" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No quizzes yet. Create your first quiz.</p>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto max-h-[500px]">
                {quizzes.map(item => {
                  const dl = deadlineLabel(item.quiz?.deadline);
                  const isSelected = selectedQuiz?.quiz?.quizId === item.quiz?.quizId;
                  return (
                    <div
                      key={item.quiz?.quizId}
                      onClick={() => handleQuizClick(item)}
                      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-2">{item.quiz?.title}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          {dl && <span className={`text-xs whitespace-nowrap ${dl.cls}`}>{dl.text}</span>}
                          <button
                            onClick={(e) => handleDelete(e, item.quiz?.quizId)}
                            className="text-muted-foreground hover:text-error"
                            title="Delete quiz"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-muted-foreground">{item.quiz?.questions?.length || 0} questions</span>
                        <span className="text-xs text-muted-foreground">{item.totalAssigned} assigned</span>
                        {item.completed > 0 && (
                          <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">{item.completed} done</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Attempts panel */}
          <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
            {!selectedQuiz ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                <Icon name="MousePointerClick" size={40} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select a quiz to view trainee results</p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">{selectedQuiz.quiz?.title}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedQuiz.quiz?.description || 'No description'}
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Deadline: {selectedQuiz.quiz?.deadline || 'None'}</span>
                    <span>Total marks: {selectedQuiz.quiz?.totalMarks}</span>
                    <span>Time limit: {selectedQuiz.quiz?.timeLimitMinutes ? `${selectedQuiz.quiz.timeLimitMinutes} min` : 'None'}</span>
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[460px]">
                  {attempts.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No attempts yet</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-muted-foreground">
                        <tr>
                          <th className="text-left font-medium px-4 py-2">Trainee</th>
                          <th className="text-left font-medium px-4 py-2">Score</th>
                          <th className="text-left font-medium px-4 py-2">%</th>
                          <th className="text-left font-medium px-4 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {attempts.map(a => {
                          const tm = selectedQuiz.quiz?.totalMarks || 0;
                          const pct = (a.status === 'COMPLETED' && tm > 0 && a.score != null)
                            ? Math.round((a.score / tm) * 100) : null;
                          return (
                            <tr key={a.attemptId}>
                              <td className="px-4 py-3">
                                <p className="font-medium text-foreground">
                                  {a.trainee?.firstname} {a.trainee?.lastname}
                                </p>
                                <p className="text-xs text-muted-foreground">{a.trainee?.emailid}</p>
                              </td>
                              <td className="px-4 py-3 text-foreground">
                                {a.status === 'COMPLETED' ? `${a.score ?? 0}/${tm}` : '-'}
                              </td>
                              <td className="px-4 py-3">
                                {pct != null ? (
                                  <span className={`font-semibold ${pctColor(pct)}`}>{pct}%</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${a.status === 'COMPLETED' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                                  {a.status === 'COMPLETED' ? 'Completed' : 'Pending'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {showCreate && (
        <CreateQuizModal
          managerId={managerId}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreateQuiz}
        />
      )}
    </div>
  );
};

export default QuizManagement;
